import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from '@/components/ui/dropdown-menu';
import { useState } from 'react';
import { CampaignSortField, CampaignSortOrder, CampaignSortOptions } from '@common-reducers/CampaignReducer';
import { CampaignStatus } from '@common/models/campaign-model';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

type Props = {
  sortOptions: CampaignSortOptions;
  onSortChange: (sortOptions: CampaignSortOptions) => void;
};

export default function CampaignSortButton({ sortOptions, onSortChange }: Props) {
  const { t } = useTranslation(namespace);
  const [open, setOpen] = useState(false);

  const sortFieldLabels: Record<CampaignSortField, string> = {
    campaignStatus: t('campaign.sort.status'),
    createdAt: t('campaign.sort.createdAt'),
    updatedAt: t('campaign.sort.lastUpdated')
  };

  const sortOrderLabels: Record<CampaignSortOrder, string> = {
    asc: t('campaign.sort.ascending'),
    desc: t('campaign.sort.descending')
  };

  // Status filter options - using the main statuses that map to display statuses
  const statusFilterOptions: Array<{ value: CampaignStatus; labelKey: string; displayStatusKey: string }> = [
    { value: 'pending', labelKey: 'campaign.status.pending', displayStatusKey: 'campaign.status.pending' },
    { value: 'running', labelKey: 'campaign.status.running', displayStatusKey: 'campaign.status.running' },
    { value: 'paused', labelKey: 'campaign.status.paused', displayStatusKey: 'campaign.status.paused' },
    { value: 'aborted', labelKey: 'campaign.status.aborted', displayStatusKey: 'campaign.status.aborted' },
    { value: 'complete-delivered', labelKey: 'campaign.status.completed', displayStatusKey: 'campaign.status.completed' },
  ];

  const handleFieldChange = (field: CampaignSortField) => {
    onSortChange({
      field,
      order: sortOptions.order,
      statusFilter: field === 'campaignStatus' ? sortOptions.statusFilter : undefined
    });
    setOpen(false);
  };

  const handleOrderChange = (order: CampaignSortOrder) => {
    onSortChange({
      field: sortOptions.field,
      order,
      statusFilter: sortOptions.statusFilter
    });
    setOpen(false);
  };

  const handleStatusFilterChange = (status: CampaignStatus | null) => {
    onSortChange({
      field: 'campaignStatus',
      order: sortOptions.order,
      statusFilter: status
    });
    setOpen(false);
  };

  const getSortIcon = () => {
    return sortOptions.order === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="lg"
          className={cn(
            "h-11 px-4",
            "bg-white dark:bg-slate-800",
            "border-slate-300 dark:border-slate-600",
            "text-slate-900 dark:text-slate-50",
            "rounded-lg",
            "font-medium text-sm",
            "whitespace-nowrap",
            "transition-all duration-200",
            "shadow-sm hover:shadow-md",
            "hover:border-slate-400 dark:hover:border-slate-500",
            "hover:bg-slate-50 dark:hover:bg-slate-700",
            "focus:border-[var(--color-accent)] dark:focus:border-[var(--color-accent)]",
            "focus:ring-2 focus:ring-[var(--color-accent)]/20"
          )}
        >
          <ArrowUpDown className="h-4 w-4 mr-2" />
          <span>
            {sortFieldLabels[sortOptions.field]}
            {sortOptions.field === 'campaignStatus' && sortOptions.statusFilter && (
              <span className="ml-1.5 text-slate-500 dark:text-slate-400">
                ({t(statusFilterOptions.find(s => s.value === sortOptions.statusFilter)?.displayStatusKey || '')})
              </span>
            )}
          </span>
          <span className="ml-1.5">{getSortIcon()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className={cn(
          "min-w-[220px]",
          "border border-slate-200 dark:border-slate-700",
          "rounded-lg shadow-lg",
          "bg-white dark:bg-slate-800",
          "p-1"
        )}
        align="start"
      >
        <DropdownMenuLabel className="text-xs font-semibold text-slate-500 dark:text-slate-400 px-2 py-1.5">
          {t('campaign.sort.sortByField')}
        </DropdownMenuLabel>
        {Object.entries(sortFieldLabels).map(([field, label]) => {
          const isStatusField = field === 'campaignStatus';
          if (isStatusField) {
            return (
              <DropdownMenuSub key={field}>
                <DropdownMenuSubTrigger
                  className={cn(
                    "px-2 py-1.5 cursor-pointer rounded-md transition-colors",
                    "text-slate-900 dark:text-slate-50",
                    "focus:bg-slate-100 dark:focus:bg-slate-700",
                    sortOptions.field === field && "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium"
                  )}
                >
                  {label}
                  {sortOptions.statusFilter && (
                    <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                      ({t(statusFilterOptions.find(s => s.value === sortOptions.statusFilter)?.displayStatusKey || '')})
                    </span>
                  )}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent
                  className={cn(
                    "min-w-[180px]",
                    "border border-slate-200 dark:border-slate-700",
                    "rounded-lg shadow-lg",
                    "bg-white dark:bg-slate-800",
                    "p-1"
                  )}
                >
                  <DropdownMenuItem
                    onClick={() => handleStatusFilterChange(null)}
                    className={cn(
                      "px-2 py-1.5 cursor-pointer rounded-md transition-colors",
                      "text-slate-900 dark:text-slate-50",
                      "focus:bg-slate-100 dark:focus:bg-slate-700",
                      !sortOptions.statusFilter && "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium"
                    )}
                  >
                    {t('campaign.sort.allStatuses')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1 border-slate-200 dark:border-slate-700" />
                  {statusFilterOptions.map((statusOption) => (
                    <DropdownMenuItem
                      key={statusOption.value}
                      onClick={() => handleStatusFilterChange(statusOption.value)}
                      className={cn(
                        "px-2 py-1.5 cursor-pointer rounded-md transition-colors",
                        "text-slate-900 dark:text-slate-50",
                        "focus:bg-slate-100 dark:focus:bg-slate-700",
                        sortOptions.statusFilter === statusOption.value && "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium"
                      )}
                    >
                      {t(statusOption.labelKey)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            );
          } else {
            return (
              <DropdownMenuItem
                key={field}
                onSelect={() => {
                  handleFieldChange(field as CampaignSortField);
                }}
                className={cn(
                  "px-2 py-1.5 cursor-pointer rounded-md transition-colors",
                  "text-slate-900 dark:text-slate-50",
                  "focus:bg-slate-100 dark:focus:bg-slate-700",
                  sortOptions.field === field && "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium"
                )}
              >
                {label}
              </DropdownMenuItem>
            );
          }
        })}
        
        <DropdownMenuSeparator className="my-1 border-slate-200 dark:border-slate-700" />
        <DropdownMenuLabel className="text-xs font-semibold text-slate-500 dark:text-slate-400 px-2 py-1.5">
          {t('campaign.sort.sortOrder')}
        </DropdownMenuLabel>
        {Object.entries(sortOrderLabels).map(([order, label]) => (
          <DropdownMenuItem
            key={order}
            onSelect={() => {
              handleOrderChange(order as CampaignSortOrder);
            }}
            className={cn(
              "px-2 py-1.5 cursor-pointer rounded-md transition-colors",
              "text-slate-900 dark:text-slate-50",
              "focus:bg-slate-100 dark:focus:bg-slate-700",
              sortOptions.order === order && "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium"
            )}
          >
            <div className="flex items-center gap-2">
              {order === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
              <span>{label}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
